import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import { Section, ErrorPanel, LoadingRow } from '../components/ui/bits';
import { Badge } from '../components/ui/Badge';
import { useRpc } from '../lib/queries';
import { SEV_META, type InboxItem } from '../lib/classes';

const ORDER: InboxItem['severity'][] = ['act', 'watch', 'info'];
const GROUP_LABEL: Record<string, string> = {
  act: 'Act now', watch: 'Worth watching', info: 'For information',
};

export const InboxPage: React.FC = () => {
  const { data, error, isLoading } = useRpc<InboxItem[]>('admin_inbox');

  if (isLoading) return <LoadingRow label="Checking every signal" />;
  if (error) return <ErrorPanel error={error} label="admin_inbox" />;

  const items = data ?? [];

  return (
    <div>
      <PageHeader title="Inbox" subtitle="Signals derived from live state. Each one clears itself when the underlying condition clears." />
      {items.length === 0 && (
        <div className="flex items-center gap-2 p-4 rounded-lg bg-emerald-50 border border-emerald-200">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          <span className="text-[13px] text-emerald-800 font-medium">Nothing needs you. Every monitored signal is quiet.</span>
        </div>
      )}
      <div className="space-y-4">
        {ORDER.map(sev => {
          const group = items.filter(i => i.severity === sev);
          if (group.length === 0) return null;
          return (
            <Section key={sev} title={GROUP_LABEL[sev]}>
              <div className="space-y-1.5">
                {group.map(i => (
                  <Link key={i.key} to={i.url} className="flex items-center gap-3 p-2.5 rounded-md border border-stone-100 hover:border-indigo-300 hover:bg-stone-50 transition-colors">
                    <Badge tone={SEV_META[i.severity].tone}>{SEV_META[i.severity].label}</Badge>
                    <span className="text-[13px] text-stone-700 flex-1">{i.title}</span>
                    <span className="text-[13px] font-bold text-stone-900 tabular-nums">{i.n}</span>
                    <ArrowRight className="w-3.5 h-3.5 text-stone-300" />
                  </Link>
                ))}
              </div>
            </Section>
          );
        })}
      </div>
    </div>
  );
};
